import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Settings, 
  TrendingUp, 
  Shield, 
  Clock, 
  DollarSign,
  BarChart3,
  Cpu,
  Database,
  AlertTriangle
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ImageWithFallback } from '../figma/ImageWithFallback';

export function PredictiveMaintenancePage() {
  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Failure Prediction",
      description: "Predict equipment failures 2-8 weeks in advance with 95%+ accuracy"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Real-time Monitoring",
      description: "24/7 monitoring of critical equipment parameters and performance metrics"
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Cost Optimization",
      description: "Reduce maintenance costs by up to 30% through optimized scheduling"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Risk Mitigation",
      description: "Minimize unplanned downtime and prevent catastrophic equipment failures"
    }
  ];

  const techStack = [
    { name: "TensorFlow", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2d/Tensorflow_logo.svg" },
    { name: "PyTorch", logo: "https://pytorch.org/assets/images/pytorch-logo.png" },
    { name: "Apache Kafka", logo: "https://kafka.apache.org/images/logo.png" },
    { name: "InfluxDB", logo: "https://www.influxdata.com/wp-content/uploads/influxdb-logo.png" },
    { name: "Kubernetes", logo: "https://kubernetes.io/images/kubernetes-horizontal-color.png" },
    { name: "AWS", logo: "https://a0.awsstatic.com/libra-css/images/logos/aws_logo_smile_1200x630.png" }
  ];

  const steps = [
    {
      step: 1,
      title: "Data Collection",
      description: "Sensors capture vibration, temperature, pressure, and operational data",
      icon: <Database className="w-8 h-8" />
    },
    {
      step: 2,
      title: "AI Analysis",
      description: "Machine learning models analyze patterns and detect anomalies",
      icon: <Cpu className="w-8 h-8" />
    },
    {
      step: 3,
      title: "Prediction & Alerts",
      description: "System generates predictions and sends actionable alerts",
      icon: <AlertTriangle className="w-8 h-8" />
    },
    {
      step: 4,
      title: "Maintenance Planning",
      description: "Optimize maintenance schedules based on predicted failures",
      icon: <BarChart3 className="w-8 h-8" />
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-15 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.05"><path d="M20 20c0 11.046-8.954 20-20 20v20h40V20H20z"/></g></g></svg>')}")`
        }} />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center mr-4">
                  <Settings className="w-6 h-6 text-black" />
                </div>
                <span className="text-primary font-semibold">Predictive Maintenance</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 neon-text">
                Prevent Failures Before They Happen
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Transform your maintenance strategy with AI-powered predictive analytics. 
                Reduce downtime, cut costs, and maximize equipment lifespan with intelligent insights.
              </p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button 
                  size="lg" 
                  className="rounded-full bg-primary text-primary-foreground hover:neon-glow px-8 py-4"
                >
                  Schedule Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full border-primary/50 hover:border-primary hover:bg-primary/10 px-8 py-4"
                >
                  Download Whitepaper
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="glass rounded-2xl p-6 relative overflow-hidden">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop"
                  alt="Predictive Maintenance Dashboard"
                  className="w-full h-64 lg:h-80 object-cover rounded-xl"
                />
                
                {/* Floating metrics */}
                <motion.div
                  className="absolute top-8 right-8 glass-strong px-4 py-2 rounded-lg"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <div className="text-accent font-bold">95%</div>
                  <div className="text-xs text-muted-foreground">Accuracy</div>
                </motion.div>
                
                <motion.div
                  className="absolute bottom-8 left-8 glass-strong px-4 py-2 rounded-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  <div className="text-primary font-bold">30%</div>
                  <div className="text-xs text-muted-foreground">Cost Reduction</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-15">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">The Challenge</h2>
              
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Traditional reactive maintenance costs manufacturers billions annually through 
                unplanned downtime, emergency repairs, and premature equipment replacement.
              </p>

              <div className="space-y-4">
                {[
                  "Unplanned downtime costs $50,000 per hour on average",
                  "Traditional maintenance wastes 30% of maintenance budget",
                  "Equipment failures cause 42% of total unplanned downtime",
                  "Manual inspection methods miss 70% of early failure signs"
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                    <div className="w-2 h-2 bg-destructive rounded-full mt-2 mr-4 flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=600&h=400&fit=crop"
                alt="Industrial machinery"
                className="w-full h-80 object-cover rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-15 bg-gradient-to-b from-background/50 to-[#111111]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Approach</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Advanced machine learning algorithms analyze equipment data to predict failures 
              weeks in advance, enabling proactive maintenance strategies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto text-black"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {step.icon}
                  </motion.div>
                  
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {step.step}
                  </div>
                </div>
                
                <h3 className="text-lg font-bold mb-3">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="glass p-6 h-full hover:border-primary/50 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="text-primary">{feature.icon}</div>
                    </div>
                    <div>
                      <h3 className="font-bold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-15 bg-[#111111]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Technology Stack</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Built on industry-leading technologies for enterprise-scale performance and reliability
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center items-center gap-8">
            {techStack.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1 }}
                className="glass p-4 rounded-lg min-w-32 text-center group cursor-pointer"
              >
                <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">{tech.name.charAt(0)}</span>
                </div>
                <p className="text-sm font-medium group-hover:text-primary transition-colors">{tech.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Story */}
      <section className="py-15">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Card className="glass p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-6">Customer Success Story</h2>
                  
                  <blockquote className="text-xl italic text-muted-foreground mb-6 border-l-4 border-primary pl-6">
                    "amasQIS reduced our unplanned downtime by 75% and saved us $2.4M annually. 
                    The predictive insights have transformed how we approach maintenance."
                  </blockquote>
                  
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                      <span className="text-black font-bold">JM</span>
                    </div>
                    <div>
                      <div className="font-bold">John Martinez</div>
                      <div className="text-muted-foreground text-sm">VP of Operations, Global Manufacturing Corp</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">75%</div>
                      <div className="text-sm text-muted-foreground">Downtime Reduction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-accent">$2.4M</div>
                      <div className="text-sm text-muted-foreground">Annual Savings</div>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&h=300&fit=crop"
                    alt="Manufacturing facility"
                    className="w-full h-64 object-cover rounded-xl"
                  />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-15 bg-gradient-to-r from-primary/10 to-secondary/10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.05"><path d="M20 20c0 11.046-8.954 20-20 20v20h40V20H20z"/></g></g></svg>')}")`
        }} />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Ready to Transform Your Maintenance?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Join hundreds of companies that have revolutionized their maintenance operations with predictive AI
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="rounded-full bg-primary text-primary-foreground hover:neon-glow px-8 py-4 text-lg"
                >
                  Talk to an Expert
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full border-primary/50 hover:border-primary hover:bg-primary/10 px-8 py-4 text-lg"
              >
                Schedule Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}